import frozenViewsBuilder from './frozenViewsBuilder';
import metadataStylesBuilder from './metadataStylesBuilder';

import columnDefsToColumnStyle from '../../utils/columnDefsToColumnStyle';
import rowTypeToRowStyle from '../../utils/rowTypeToRowStyle';
import exportFormatsToCustomFormats from '../../utils/exportFormatsToCustomFormats';

import { customClassesToRowStyleMap, customClassToRowStyleMapValuesOnly } from '../../constants/customClasses';

const buildRowStyles = (rows, metadataLength, { hideDecimals, shouldDisplayZero, negativeMinus }) => {
  const metadataStyles = metadataStylesBuilder(metadataLength);

  const rowStyles = rows.map(row => {
    let rowStyle = {};

    const customRowStyles = row.customClasses
      ? row.customClasses.reduce((acc, customClass) => ({ ...acc, ...customClassesToRowStyleMap[customClass] }), {})
      : {};

    if (row.rowType) {
      rowStyle = {
        ...rowStyle,
        ...rowTypeToRowStyle(row.rowType, { hideDecimals, shouldDisplayZero, negativeMinus }),
        ...customRowStyles,
      };
    }

    if (row.$style) {
      rowStyle = {
        ...rowStyle,
        ...row.$style,
      };
    }

    if (row.format) {
      rowStyle.numFmt = exportFormatsToCustomFormats(row.format, { hideDecimals, shouldDisplayZero, negativeMinus });
    }

    return rowStyle;
  });

  return [...Object.values(metadataStyles), ...rowStyles];
};

const createEmptyColumnDefinitions = count => new Array(count).fill({});

const buildColumnStyles = ({
  columnDefinitions = [],
  columnCount,
  withPreText = false,
  descriptionColumnIndex,
  splitDescriptionColumn,
}) => {
  const descriptionColumns = withPreText ? 2 : 1;
  const splittedColumn = splitDescriptionColumn ? 1 : 0;
  const columnCountWithoutColumnDefs
    = columnCount - columnDefinitions.length + descriptionColumns;
  const tagNameColumn = 1;

  const emptyColumnDefinitionsLength = columnCountWithoutColumnDefs
    + splittedColumn
    + tagNameColumn;
  const emptyColumnDefinitions = createEmptyColumnDefinitions(
    emptyColumnDefinitionsLength < 0 ? 0 : emptyColumnDefinitionsLength
  );

  const allColumnDefs = [...columnDefinitions, ...emptyColumnDefinitions];

  const columnStyles = columnDefsToColumnStyle(allColumnDefs, descriptionColumnIndex);

  return columnStyles;
};

const buildCellStyles = (rows, metadataLength) =>
  rows.reduce((cellsStyles, row, rowIndex) => {
    const customValueOnlyStyles = row.customClasses
      ? row.customClasses.reduce((acc, customClass) => ({
        ...acc,
        ...customClassToRowStyleMapValuesOnly[customClass],
      }), {})
      : {};

    if (Object.keys(customValueOnlyStyles).length) {
      row.columns.forEach((column, columnIndex) => {
        if (!columnIndex) {
          return;
        }
        cellsStyles.push({
          address: { row: rowIndex + metadataLength, column: columnIndex },
          style: customValueOnlyStyles,
        });
      });
    }

    return cellsStyles;
  }, []);

const reportStyleBuilder = params => {
  const {
    sheetName,
    numberOfRowsToStick,
    numberOfColumnsToStick,
    columnCount,
    columnDefinitions,
    rows,
    sheetMetadataLength,
    descriptionColumnIndex,
    withPreText,
    hideDecimals,
    shouldDisplayZero,
    negativeMinus,
    splitDescriptionColumn,
  } = params;

  const maxRowOutlineLevel = Math.max(...rows.map(({ $style }) => $style?.outlineLevel ?? 0));

  const metadata = {
    sheetName,
    properties: {
      ...maxRowOutlineLevel
        ? {
          outlineLevelRow: maxRowOutlineLevel,
        }
        : {},
    },
    views: frozenViewsBuilder({
      rows: numberOfRowsToStick,
      columns: numberOfColumnsToStick,
    }),
    columnStyles: buildColumnStyles({
      columnDefinitions,
      columnCount,
      descriptionColumnIndex,
      withPreText,
      splitDescriptionColumn,
    }),
    rowStyles: buildRowStyles(rows, sheetMetadataLength, { hideDecimals, shouldDisplayZero, negativeMinus }),
    cellStyles: buildCellStyles(rows, sheetMetadataLength, { hideDecimals, shouldDisplayZero, negativeMinus }),
  };

  return metadata;
};

export default reportStyleBuilder;
